import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { Dialog, Grid, Box, Typography, Divider } from '@material-ui/core';
import { Pagination } from '@material-ui/lab';
import { GroupOutlined, EditOutlined, VpnKeyOutlined, VerifiedUserOutlined } from '@material-ui/icons';
import Loader from 'components/Loader';
import Input from 'components/Input';
import Button from 'components/Button';
import PasswordUpdate from 'components/PasswordUpdate';

import { loadUserNewPasswordTerms } from 'pages/Profile/store/thunk';
import { loadEmployee, updateEmployeePassword } from 'pages/EmployeesRegistration/store/thunk';
import { recoverPassword } from 'pages/PasswordRecovery/store/thunk';

import useWindowDimensions from 'helpers/hooks/useWindowDimensions';

export default function PasswordUsersUpdate({ isOpen, setIsOpen, users }) {
  const dispatch = useDispatch();

  const { width } = useWindowDimensions();
  const isSm = width < 960;

  const { userId, isLoading } = useSelector(state => state.profile);
  const { isLoading: isEmployeeLoading } = useSelector(state => state.employeesRegistration);

  const pageSize = 5;

  const [page, setPage] = useState(1);
  const [userSearch, setUserSearch] = useState('');
  const [isPasswordUpdateOpen, setIsPasswordUpdateOpen] = useState(false);
  const [isPasswordUpdateLoading, setIsPasswordUpdateLoading] = useState(false);
  const [employeeId, setEmployeeId] = useState('');

  const userList = users?.filter(user => {
    if(userSearch) {
      return user?.name?.includes(userSearch) || user?.email?.includes(userSearch);
    }

    return user;
  });

  const userDataPage = userList?.slice(pageSize * page - pageSize, pageSize * page);
  const userPageCount = !!userSearch ? Math.ceil(userList?.length / pageSize) : Math.ceil(users?.length / pageSize);

  useEffect(() => {
    if(users?.length == 0) {
      setIsOpen(false);
    }
  }, [users]);

  const handleEditUserPassword = employeeId => {
    dispatch(loadEmployee(userId, employeeId));
    setEmployeeId(employeeId);
    setIsPasswordUpdateOpen(true);
  }

  const handleSendUserRecoverEmail = email => {
    if(!email) {
      return toast.error('Usuário não possui email.');
    }

    dispatch(recoverPassword({ email }, false));
  }

  return(
    <Dialog
      fullWidth
      maxWidth='md'
      id='password-update-warning-modal'
      open={isOpen}
      onClose={() => setIsOpen(false)}
      PaperProps={{
        style: {
          height: 'fit-content',
          overflowX: 'hidden'
        }
      }}
    >
      {isPasswordUpdateOpen && (
        <PasswordUpdate
          isEmployeeEdit
          isLoading={isPasswordUpdateLoading || isEmployeeLoading}
          isOpen={isPasswordUpdateOpen}
          setIsOpen={setIsPasswordUpdateOpen}
          onSuccess={values => {
            setIsPasswordUpdateLoading(true);

            dispatch(updateEmployeePassword(userId, employeeId, values))
              .then(() => {
                dispatch(loadUserNewPasswordTerms(userId));
                setIsPasswordUpdateOpen(false);
              })
              .finally(() => setIsPasswordUpdateLoading(false));
          }}
        />
      )}
      <Grid container style={{ padding: 30 }}>
        <Grid item xs={12}>
          <Grid container xs={12} justify='center'>
            <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '60px', height: '60px', backgroundColor: 'rgba(2, 42, 92, 0.2)', borderRadius: '50%' }}>
              <GroupOutlined fontSize='large' style={{ color: '#022A5C' }} />
            </Box>
          </Grid>
          <Grid container xs={12} direction='column' alignItems='center' style={{ marginTop: 10 }}>
            <Typography variant='h5'>
              Migração de senha forte dos usuários
            </Typography>
            <Typography variant='body2' color='textSecondary' align='center' style={{ marginTop: 10 }}>
              Gerencie os usuários ativos pendentes e solicite a atualização das credenciais com segurança.
            </Typography>
          </Grid>
        </Grid>
        <Grid item xs={12} style={{ marginTop: 20 }}>
          <Loader isLoading={isLoading}>
            <Box>
              <Input
                id="password-user-update-search"
                type="text"
                name="userSearch"
                label="Buscar por nome ou e-mail"
                value={userSearch}
                onChange={event => setUserSearch(event.target.value)}
              />
            </Box>
            <Box
              style={{
                marginTop: 10,
                backgroundColor: '#FFFFFF',
                border: '1px solid #E6EAF2',
                borderRadius: 16,
                overflowX: 'auto',
                width: '100%'
              }}
            >
              <Box
                style={{
                  display: 'grid',
                  gridTemplateColumns: isSm ? 'minmax(0, 1fr) minmax(0, 1fr)' : 'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1.55fr)',
                  gap: 16,
                  alignItems: 'center',
                  width: '100%',
                  padding: '18px 20px',
                  backgroundColor: '#F8FAFD',
                  borderBottom: '1px solid #E6EAF2'
                }}
              >
                {isSm ? (
                  <Typography variant='body1' style={{ fontWeight: 700, color: '#252B3A' }}>
                    Usuário
                  </Typography>
                ) : (
                  <>
                    <Typography variant='body1' style={{ fontWeight: 700, color: '#252B3A' }}>
                      Nome
                    </Typography>
                    <Typography variant='body1' style={{ fontWeight: 700, color: '#252B3A', justifySelf: 'start' }}>
                      Email
                    </Typography>
                  </>
                )}
                <Typography variant='body1' style={{ fontWeight: 700, color: '#252B3A', justifySelf: 'end' }}>
                  Ações
                </Typography>
              </Box>
              {userDataPage.map((user, index) => (
                <Box
                  key={`${user.email}-${index}`}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: isSm ? 'minmax(0, 1fr) minmax(0, 1fr)' : 'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1.55fr)',
                    gap: 16,
                    alignItems: 'center',
                    width: '100%',
                    padding: '18px 20px',
                    borderBottom: index < userDataPage.length - 1 ? '1px solid #E6EAF2' : 'none'
                  }}
                >
                  <Typography variant='body1' style={{ fontWeight: 700, color: '#252B3A', lineBreak: isSm ? 'anywhere' : 'auto' }}>
                    {user.name}
                  </Typography>
                  {isSm && (
                    <br />
                  )}
                  <Typography variant='body1' style={{ color: '#8B96A8', lineBreak: isSm ? 'anywhere' : 'auto' }}>
                    {user.email}
                  </Typography>
                  <Box style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12 }}>
                    <Button
                      type='button'
                      variant='outlined'
                      color='tertiary'
                      startIcon={<EditOutlined fontSize='small' />}
                      style={{ whiteSpace: 'nowrap' }}
                      onClick={() => handleEditUserPassword(user?.userId)}
                    >
                      {!isSm && (
                        <Typography variant='caption' color='textSecondary'>
                          Editar
                        </Typography>
                      )}
                    </Button>
                    <Button
                      type='button'
                      color='primary'
                      startIcon={<VpnKeyOutlined fontSize='small' />}
                      style={{ whiteSpace: 'nowrap' }}
                      onClick={() => handleSendUserRecoverEmail(user?.email)}
                    >
                      {!isSm && (
                        <Typography variant='caption'>
                          Enviar recuperação de senha
                        </Typography>
                      )}
                    </Button>
                  </Box>
                </Box>
              ))}
            </Box>
            <Box style={{ display: 'flex', justifyContent: 'center', marginTop: 20 }}>
              <Pagination
                color='primary'
                variant='outlined'
                shape='rounded'
                count={userPageCount}
                page={page}
                onChange={(_, page) => setPage(page)}
              />
            </Box>
          </Loader>
        </Grid>
        <Grid item xs={12} style={{ marginTop: 20 }}>
          <Divider />
        </Grid>
        <Grid container justify='flex-start' alignItems='center' xs={12} style={{ marginTop: 20, gap: 20 }}>
          <Box style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <EditOutlined fontSize='small' style={{ color: '#757575' }} />
            <Typography variant='caption' color='textSecondary'>
              Ícone de lápis: editar senha.
            </Typography>
          </Box>
          <Box style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <VpnKeyOutlined fontSize='small' style={{ color: '#757575' }} />
            <Typography variant='caption' color='textSecondary'>
              Ícone de envio/chave: enviar recuperação de senha.
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} style={{ marginTop: 20 }}>
          <Divider />
        </Grid>
        <Grid container xs={12} justify='space-between' alignItems='center' style={{ marginTop: 20 }}>
          <Box style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <VerifiedUserOutlined color='primary' fontSize='small' />
            <Typography variant='caption' color='textSecondary'>
              {users?.length} usuário(s) pendente(s)
            </Typography>
          </Box>
          <Button
            type='button'
            color='success'
            onClick={() => setIsOpen(false)}
          >
            Concluir
          </Button>
        </Grid>
      </Grid>
    </Dialog>
  );
}